/**
 * Sustituto de window.prompt / window.confirm para Super Admin.
 * Misma API mental: preguntar → valor o null si cancela.
 */
import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export function PromptDialog({ open, title, description, defaultValue = '',
  placeholder = '', confirmLabel = 'Continuar', cancelLabel = 'Cancelar',
  multiline = false, required = true, onConfirm, onCancel }) {
  const [valor, setValor] = useState(defaultValue || '');
  const ref = useRef(null);

  useEffect(() => {
    if (open) {
      setValor(defaultValue || '');
      const t = setTimeout(() => ref.current?.focus?.(), 50);
      return () => clearTimeout(t);
    }
  }, [open, defaultValue]);

  const aceptar = () => {
    const v = (valor || '').trim();
    if (required && !v) return;
    onConfirm?.(v);
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">{title || 'Confirmar'}</DialogTitle>
          {description && (
            <DialogDescription className="whitespace-pre-wrap text-left text-sm">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {multiline ? (
          <Textarea ref={ref} value={valor} onChange={(e) => setValor(e.target.value)}
                    placeholder={placeholder} rows={4} className="text-sm" />
        ) : (
          <Input ref={ref} value={valor} onChange={(e) => setValor(e.target.value)}
                 placeholder={placeholder} className="text-sm"
                 onKeyDown={(e) => { if (e.key === 'Enter') aceptar(); }} />
        )}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onCancel?.()}>
            {cancelLabel}
          </Button>
          <Button type="button" onClick={aceptar}
                  disabled={required && !(valor || '').trim()}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Estado + helpers: const { ask, confirm, ui } = usePrompt(); … {ui} */
export function usePrompt() {
  const [cfg, setCfg] = useState(null);
  const resolver = useRef(null);

  const cerrar = (valor) => {
    const r = resolver.current;
    resolver.current = null;
    setCfg(null);
    r?.(valor);
  };

  const ask = (opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setCfg({
      title: opts.title || 'Escribe un valor',
      description: opts.description || '',
      defaultValue: opts.defaultValue || '',
      placeholder: opts.placeholder || '',
      confirmLabel: opts.confirmLabel || 'Continuar',
      cancelLabel: opts.cancelLabel || 'Cancelar',
      multiline: !!opts.multiline,
      required: opts.required !== false,
    });
  });

  const confirm = (opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setCfg({
      title: opts.title || '¿Seguimos?',
      description: opts.description || '',
      defaultValue: opts.defaultValue ?? 'sí',
      placeholder: opts.placeholder || 'Escribe sí para confirmar',
      confirmLabel: opts.confirmLabel || 'Sí, continuar',
      cancelLabel: opts.cancelLabel || 'Cancelar',
      multiline: false,
      required: true,
      _confirmMode: true,
      _confirmWord: (opts.confirmWord || 'sí').toLowerCase(),
    });
  });

  const onConfirm = (v) => {
    if (cfg?._confirmMode) {
      if ((v || '').trim().toLowerCase() !== cfg._confirmWord) {
        return; // deja abierto
      }
      cerrar(true);
      return;
    }
    cerrar(v);
  };

  const ui = (
    <PromptDialog
      open={!!cfg}
      title={cfg?.title}
      description={cfg?.description}
      defaultValue={cfg?.defaultValue}
      placeholder={cfg?.placeholder}
      confirmLabel={cfg?.confirmLabel}
      cancelLabel={cfg?.cancelLabel}
      multiline={cfg?.multiline}
      required={cfg?.required}
      onConfirm={onConfirm}
      onCancel={() => cerrar(cfg?._confirmMode ? false : null)}
    />
  );

  return { ask, confirm, ui };
}
